import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DigitalCard } from '@/components/DigitalCard';
import { Download, FileImage, FileText, IdCard, Loader2 } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export default function MyCard() {
  const { t } = useI18n();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPng, setDownloadingPng] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get('/cards/my').then(r => setCards(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const downloadPNG = async () => {
    if (!cardRef.current) return;
    setDownloadingPng(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, backgroundColor: null });
      const link = document.createElement('a');
      link.download = `carte-diaspora-${cards[0]?.card_number || 'card'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Carte telechargee en PNG');
    } catch {
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloadingPng(false);
    }
  };

  const downloadPDF = async () => {
    if (!cardRef.current) return;
    setDownloadingPdf(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, backgroundColor: null });
      const imgData = canvas.toDataURL('image/png');
      // Card format: credit card size (85.6mm x 53.98mm)
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [85.6, 53.98] });
      pdf.addImage(imgData, 'PNG', 0, 0, 85.6, 53.98);
      pdf.save(`carte-diaspora-${cards[0]?.card_number || 'card'}.pdf`);
      toast.success('Carte telechargee en PDF');
    } catch {
      toast.error('Erreur lors du telechargement PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="my-card-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('card.title')}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t('card.desc')}</p>
      </div>

      {cards.length === 0 ? (
        <Card className="border">
          <CardContent className="p-8 text-center space-y-3">
            <IdCard className="w-12 h-12 mx-auto text-muted-foreground/40" />
            <p className="font-semibold">{t('card.none')}</p>
            <p className="text-sm text-muted-foreground">{t('card.none_desc')}</p>
          </CardContent>
        </Card>
      ) : (
        cards.map(card => (
          <div key={card.card_id} className="space-y-4">
            <div ref={cardRef} className="flex justify-center">
              <DigitalCard card={card} />
            </div>

            <Card className="border">
              <CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t('card.number')}</span>
                    <p className="font-mono font-medium">{card.card_number}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('card.status')}</span>
                    <div className="mt-1">
                      <Badge className={card.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                        {card.is_active ? t('card.active') : t('card.inactive')}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('card.issued')}</span>
                    <p className="font-medium">{card.issued_at ? new Date(card.issued_at).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('card.expires')}</span>
                    <p className="font-medium">{card.expires_at ? new Date(card.expires_at).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    className="flex-1 bg-[#007FFF] hover:bg-[#0066CC]"
                    onClick={downloadPNG}
                    disabled={downloadingPng}
                    data-testid="download-png-btn"
                  >
                    {downloadingPng ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileImage className="w-4 h-4 mr-2" />}
                    {t('card.download_png')}
                  </Button>
                  <Button
                    className="flex-1 bg-[#009543] hover:bg-[#007A36]"
                    onClick={downloadPDF}
                    disabled={downloadingPdf}
                    data-testid="download-pdf-btn"
                  >
                    {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                    {t('card.download_pdf')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ))
      )}
    </div>
  );
}
